import React, { useState } from 'react'
import { Box, Button, Link, Rating, Skeleton, Typography } from '@mui/material'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { C, cardSx, tabular } from './tokens'
import { count, dateTimeIn, money } from './format'
import { cardSubSx, cardTitleSx } from './SalesOverTime'

export const CHANNEL_LABEL = { ONLINE: 'Online', OLD_RMF: 'Legacy app', PARTNER: 'Partner', INSTORE: 'In-store' }

// Where the store's customer page lives — the same host EmbedLinks uses.
const WEB_APP_URL = (process.env.REACT_APP_WEB_APP_URL || 'https://app.rushmyfood.com').replace(/\/+$/, '')

function statusBadge(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'REFUNDED') return { label: 'Refunded', color: C.amberText, bg: C.amberSoft }
  if (['CANCELLED', 'FAILED'].includes(s)) return { label: 'Cancelled', color: '#4B5563', bg: '#F3F4F6' }
  if (['DELIVERED', 'COMPLETED', 'PICKED', 'PICKED_UP'].includes(s)) return { label: 'Completed', color: C.upText, bg: C.upSoft }
  return { label: 'In progress', color: C.skyText, bg: C.skySoft }
}

function Shell({ title, sub, action, children }) {
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', minHeight: 320, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography component="h2" sx={cardTitleSx}>{title}</Typography>
          <Typography sx={cardSubSx}>{sub}</Typography>
        </Box>
        {action}
      </Box>
      <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  )
}

const rowsSkeleton = n => Array.from({ length: n }).map((_, i) => <Skeleton key={i} height={36} />)
const none = text => <Typography sx={{ fontSize: 13, color: C.textMuted, py: 2 }}>{text}</Typography>

export function TopItems({ data, loading }) {
  const items = data?.topItems || []
  const max = Math.max(1, ...items.map(i => i.revenue))
  return (
    <Shell title="Top items" sub="Best sellers by revenue this period">
      {loading && !data ? rowsSkeleton(5) : items.length === 0 ? none('No items sold in this range.') : (
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, ...tabular }}>
          {items.map((it, i) => (
            <Box component="li" key={it.name} sx={{ py: 1, borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 14 }}>
                <Box component="span" sx={{ color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.name}>{it.name}</Box>
                <Box component="span" sx={{ color: C.text, fontWeight: 500, flexShrink: 0 }}>{money(it.revenue)}</Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <Box sx={{ flex: 1, height: 6, bgcolor: '#F1F2F4', borderRadius: 3, overflow: 'hidden' }} aria-hidden>
                  <Box sx={{ width: `${(it.revenue / max) * 100}%`, height: '100%', bgcolor: C.brand, borderRadius: 3 }} />
                </Box>
                <Typography sx={{ fontSize: 12, color: C.textMuted, minWidth: 92, textAlign: 'right', ...tabular }}>
                  {count(it.quantity)} sold · {it.share.toFixed(0)}%
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Shell>
  )
}

/* Each row opens that order on the Orders page (?open=o:12 / e:34 / c:56). */
export function RecentOrders({ data, loading, tz }) {
  const orders = data?.recentOrders || []
  return (
    <Shell
      title="Recent orders"
      sub="The latest orders from every channel"
      action={<Link href="#/admin/orders" underline="hover" sx={{ fontSize: 13, fontWeight: 600, color: C.brandText, whiteSpace: 'nowrap' }}>View all orders</Link>}>
      {loading && !data ? rowsSkeleton(5) : orders.length === 0 ? none('No orders yet.') : (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mx: -1, ...tabular }}>
          {orders.map((o, i) => {
            const b = statusBadge(o.status)
            return (
              <Box component="li" key={o.id} sx={{ borderBottom: i < orders.length - 1 ? `1px solid ${C.border}` : 0 }}>
                <Box
                  component="a"
                  href={`#/admin/orders?open=${encodeURIComponent(o.id)}`}
                  aria-label={`Open order ${o.number}, ${money(o.total)}, ${b.label}`}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1.1, borderRadius: '8px',
                    textDecoration: 'none', color: 'inherit',
                    '&:hover': { bgcolor: '#F6F7F9' },
                    '&:focus-visible': { outline: `2px solid ${C.brandText}`, outlineOffset: -2 }
                  }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                      <Box component="span" sx={{ color: C.text, fontWeight: 600 }}>#{o.number}</Box>
                      <Box component="span" sx={{ color: C.text, fontWeight: 500 }}>{money(o.total)}</Box>
                    </Box>
                    <Box sx={{ fontSize: 12, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {dateTimeIn(o.placedAt, tz)} · {CHANNEL_LABEL[o.channel] || o.channel}
                    </Box>
                  </Box>
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: b.color, bgcolor: b.bg, px: 1, py: 0.25, borderRadius: '999px', whiteSpace: 'nowrap' }}>{b.label}</Box>
                  <ChevronRightIcon sx={{ fontSize: 18, color: C.muted }} aria-hidden />
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
    </Shell>
  )
}

export function RatingsSnapshot({ data, loading, tz }) {
  const r = data?.ratings
  const [copied, setCopied] = useState(false)
  const slug = data?.restaurantSlug
  const storeUrl = slug ? `${WEB_APP_URL}/#/restaurant/${encodeURIComponent(slug)}` : null

  const share = async () => {
    if (!storeUrl) return
    try {
      if (navigator.share) await navigator.share({ title: data?.restaurantName || 'Our menu', url: storeUrl })
      else {
        await navigator.clipboard.writeText(storeUrl)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (_) { /* dismissed share sheet */ }
  }

  return (
    <Shell
      title="Ratings"
      sub="All-time customer ratings"
      action={r && r.count ? <Link href="#/admin/ratings" underline="hover" sx={{ fontSize: 13, fontWeight: 600, color: C.brandText, whiteSpace: 'nowrap' }}>See reviews</Link> : null}>
      {loading && !data ? rowsSkeleton(4) : !r || !r.count ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 0.75, py: 2 }}>
          <StarBorderRoundedIcon sx={{ fontSize: 36, color: C.muted }} aria-hidden />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>No ratings yet</Typography>
          <Typography sx={{ fontSize: 13, color: C.textMuted, maxWidth: 240 }}>Ratings appear after customers rate completed orders</Typography>
          {storeUrl ? (
            <Button
              size="small"
              variant="text"
              onClick={share}
              startIcon={<LinkRoundedIcon />}
              sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600, color: C.brandText, '&:hover': { bgcolor: C.brandFill } }}>
              {copied ? 'Link copied' : 'Share your store link'}
            </Button>
          ) : null}
        </Box>
      ) : (
        <Box sx={tabular}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: 32, fontWeight: 600, color: C.text, lineHeight: 1, ...tabular }}>{r.average.toFixed(1)}</Typography>
            <Typography sx={{ fontSize: 13, color: C.textMuted }}>out of 5</Typography>
          </Box>
          <Rating value={r.average} precision={0.1} readOnly size="small" sx={{ mt: 1, color: '#F59E0B' }} aria-label={`${r.average.toFixed(1)} out of 5 stars`} />
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.5 }}>{count(r.count)} rating{r.count === 1 ? '' : 's'}</Typography>
          {r.latestText ? (
            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${C.border}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Latest review</Typography>
                {r.latestRating ? <Rating value={r.latestRating} readOnly size="small" sx={{ color: '#F59E0B', fontSize: 14 }} aria-label={`${r.latestRating} stars`} /> : null}
              </Box>
              <Typography sx={{ fontSize: 14, color: C.text, mt: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                “{r.latestText}”
              </Typography>
              {r.latestAt ? <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.5 }}>{dateTimeIn(r.latestAt, tz)}</Typography> : null}
            </Box>
          ) : null}
        </Box>
      )}
    </Shell>
  )
}
